import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi, formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { User, Lock, AlertTriangle, Save, Mail, Building, MapPin, Trash2 } from 'lucide-react';

const initialProfile = (u) => ({
  first_name: u?.first_name || '',
  last_name: u?.last_name || '',
  company: u?.company || '',
  vat_number: u?.vat_number || '',
  siret: u?.siret || '',
  address_1: u?.address_1 || u?.billing?.address_1 || '',
  address_2: u?.address_2 || u?.billing?.address_2 || '',
  postcode: u?.postcode || u?.billing?.postcode || '',
  city: u?.city || u?.billing?.city || '',
  country: u?.country || u?.billing?.country || 'FR',
  phone: u?.phone || '',
  contact_email: u?.contact_email || u?.billing?.email || '',
});

const ProfilePanel = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => initialProfile(user));
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  const [delConfirm, setDelConfirm] = useState(false);
  const [delForm, setDelForm] = useState({ password: '', confirm: '' });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setProfile(initialProfile(user)); }, [user]);

  const onProfileChange = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    setSavingProfile(true);
    try {
      await authApi.updateProfile(profile);
      await refreshUser?.();
      toast.success('Profil mis à jour');
    } catch (err) {
      toast.error(formatApiError(err) || 'Impossible de mettre à jour le profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePwd = async (e) => {
    e?.preventDefault?.();
    if (pwd.new_password.length < 8) {
      toast.error('Le nouveau mot de passe doit faire au moins 8 caractères');
      return;
    }
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error('La confirmation ne correspond pas');
      return;
    }
    setChangingPwd(true);
    try {
      await authApi.changePassword(pwd.old_password, pwd.new_password);
      setPwd({ old_password: '', new_password: '', confirm_password: '' });
      toast.success('Mot de passe modifié');
    } catch (err) {
      toast.error(formatApiError(err) || 'Échec du changement de mot de passe');
    } finally {
      setChangingPwd(false);
    }
  };

  const onDelete = async (e) => {
    e?.preventDefault?.();
    if (delForm.confirm !== 'SUPPRIMER') {
      toast.error('Tapez exactement SUPPRIMER pour confirmer');
      return;
    }
    setDeleting(true);
    try {
      await authApi.deleteAccount(delForm.password, delForm.confirm);
      await logout?.();
      navigate('/compte-supprime', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || 'Impossible de supprimer le compte');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="profile-panel">
      {/* Identity & company */}
      <Card className="border border-border rounded-none">
        <CardHeader className="flex flex-row items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          <CardTitle className="text-lg">Mon profil</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Prénom</Label>
                <Input id="first_name" data-testid="profile-first-name" value={profile.first_name} onChange={onProfileChange('first_name')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="last_name">Nom</Label>
                <Input id="last_name" data-testid="profile-last-name" value={profile.last_name} onChange={onProfileChange('last_name')} className="rounded-none" />
              </div>
            </div>

            <Separator />
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Building className="w-3 h-3" /> Société (optionnel)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company">Société</Label>
                <Input id="company" data-testid="profile-company" value={profile.company} onChange={onProfileChange('company')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" data-testid="profile-phone" value={profile.phone} onChange={onProfileChange('phone')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="vat_number">Numéro de TVA intracom.</Label>
                <Input id="vat_number" data-testid="profile-vat" value={profile.vat_number} onChange={onProfileChange('vat_number')} placeholder="FRXX999999999" className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="siret">SIRET</Label>
                <Input id="siret" data-testid="profile-siret" value={profile.siret} onChange={onProfileChange('siret')} placeholder="14 chiffres" className="rounded-none" />
              </div>
            </div>

            <Separator />
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><MapPin className="w-3 h-3" /> Adresse postale</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="address_1">Rue</Label>
                <Input id="address_1" data-testid="profile-address-1" value={profile.address_1} onChange={onProfileChange('address_1')} className="rounded-none" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address_2">Complément d'adresse</Label>
                <Input id="address_2" data-testid="profile-address-2" value={profile.address_2} onChange={onProfileChange('address_2')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="postcode">Code postal</Label>
                <Input id="postcode" data-testid="profile-postcode" value={profile.postcode} onChange={onProfileChange('postcode')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="city">Ville</Label>
                <Input id="city" data-testid="profile-city" value={profile.city} onChange={onProfileChange('city')} className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="country">Pays</Label>
                <Input id="country" data-testid="profile-country" value={profile.country} onChange={onProfileChange('country')} className="rounded-none" />
              </div>
            </div>

            <Separator />
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Mail className="w-3 h-3" /> Emails</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Email de connexion</Label>
                <Input value={user?.email || ''} disabled className="rounded-none bg-gray-50" data-testid="profile-login-email" />
                <p className="text-[11px] text-muted-foreground mt-1">L'email de connexion ne peut pas être modifié.</p>
              </div>
              <div>
                <Label htmlFor="contact_email">Email de contact (facturation)</Label>
                <Input id="contact_email" data-testid="profile-contact-email" type="email" value={profile.contact_email} onChange={onProfileChange('contact_email')} className="rounded-none" />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" className="rounded-none" disabled={savingProfile} data-testid="profile-save-btn">
                <Save className="w-4 h-4 mr-2" />{savingProfile ? 'Sauvegarde…' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card className="border border-border rounded-none">
        <CardHeader className="flex flex-row items-center gap-2">
          <Lock className="w-4 h-4 text-primary" />
          <CardTitle className="text-lg">Changer de mot de passe</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onChangePwd} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="old_password">Mot de passe actuel</Label>
              <Input id="old_password" data-testid="pwd-old" type="password" value={pwd.old_password} onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })} required className="rounded-none" />
            </div>
            <div>
              <Label htmlFor="new_password">Nouveau mot de passe</Label>
              <Input id="new_password" data-testid="pwd-new" type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} required minLength={8} className="rounded-none" />
              <p className="text-[11px] text-muted-foreground mt-1">8 caractères minimum.</p>
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmation du nouveau mot de passe</Label>
              <Input id="confirm_password" data-testid="pwd-confirm" type="password" value={pwd.confirm_password} onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })} required minLength={8} className="rounded-none" />
            </div>
            <Button type="submit" className="rounded-none" disabled={changingPwd} data-testid="pwd-save-btn">
              <Lock className="w-4 h-4 mr-2" />{changingPwd ? 'Modification…' : 'Modifier le mot de passe'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border border-red-300 rounded-none bg-red-50/30">
        <CardHeader className="flex flex-row items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <CardTitle className="text-lg text-red-700">Zone dangereuse</CardTitle>
        </CardHeader>
        <CardContent>
          {!delConfirm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Supprimer mon compte</p>
                <p className="text-sm text-muted-foreground">Action définitive — vos devis et projets seront supprimés.</p>
              </div>
              <Button variant="outline" className="rounded-none border-red-400 text-red-700 hover:bg-red-100" onClick={() => setDelConfirm(true)} data-testid="delete-account-open-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Effacer mon compte
              </Button>
            </div>
          ) : (
            <form onSubmit={onDelete} className="space-y-4 max-w-md">
              <p className="text-sm text-red-700 font-medium">Vous êtes sur le point de supprimer définitivement votre compte. Cette action est <strong>irréversible</strong>.</p>
              <div>
                <Label htmlFor="del_password">Mot de passe actuel</Label>
                <Input id="del_password" data-testid="delete-password" type="password" value={delForm.password} onChange={(e) => setDelForm({ ...delForm, password: e.target.value })} required className="rounded-none" />
              </div>
              <div>
                <Label htmlFor="del_confirm">Tapez <span className="font-mono font-bold">SUPPRIMER</span> pour confirmer</Label>
                <Input id="del_confirm" data-testid="delete-confirm" value={delForm.confirm} onChange={(e) => setDelForm({ ...delForm, confirm: e.target.value })} required placeholder="SUPPRIMER" className="rounded-none" />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="rounded-none" onClick={() => { setDelConfirm(false); setDelForm({ password: '', confirm: '' }); }} data-testid="delete-cancel-btn">
                  Annuler
                </Button>
                <Button type="submit" disabled={deleting || delForm.confirm !== 'SUPPRIMER'} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="delete-account-confirm-btn">
                  <Trash2 className="w-4 h-4 mr-2" />{deleting ? 'Suppression…' : 'Supprimer définitivement'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePanel;
